import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TOOLTIP_OFFSET = 8;
const VIEWPORT_MARGIN = 12;

const GridTooltipContext = createContext({
    showTooltip: null,
    hideTooltip: null,
});

function normalizeRect(rect) {
    if (!rect) return null;
    return {
        top: Number(rect.top) || 0,
        right: Number(rect.right) || 0,
        bottom: Number(rect.bottom) || 0,
        left: Number(rect.left) || 0,
        width: Number(rect.width) || 0,
        height: Number(rect.height) || 0,
    };
}

function getTooltipPosition(rect) {
    const viewportWidth = typeof window === "undefined" ? 0 : window.innerWidth;
    const viewportHeight = typeof window === "undefined" ? 0 : window.innerHeight;
    const centeredLeft = rect.left + rect.width / 2;
    const left = viewportWidth > 0
        ? Math.min(Math.max(centeredLeft, VIEWPORT_MARGIN), viewportWidth - VIEWPORT_MARGIN)
        : centeredLeft;
    const hasTopSpace = rect.top > 120;
    const hasBottomSpace = viewportHeight === 0 || rect.bottom + 96 <= viewportHeight;
    const placement = hasBottomSpace || !hasTopSpace ? "bottom" : "top";
    const top = placement === "bottom" ? rect.bottom + TOOLTIP_OFFSET : rect.top - TOOLTIP_OFFSET;

    return { top, left, placement };
}

function GridTooltipPortal({ tooltipRef, state }) {
    if (!state.visible || typeof document === "undefined") return null;

    return createPortal(
        <div
            ref={tooltipRef}
            className={`grid-core-tooltip grid-core-tooltip-${state.placement}`}
            role="tooltip"
            style={{ top: state.top, left: state.left }}
        >
            {state.content}
        </div>,
        document.body,
    );
}

export function useGridTooltip() {
    const tooltipRef = useRef(null);
    const [state, setState] = useState({
        visible: false,
        content: null,
        top: 0,
        left: 0,
        placement: "bottom",
    });

    const showTooltip = useCallback((content, rect) => {
        const normalizedRect = normalizeRect(rect);
        if (!content || !normalizedRect) return;

        setState({
            visible: true,
            content,
            ...getTooltipPosition(normalizedRect),
        });
    }, []);

    const hideTooltip = useCallback(() => {
        setState((current) => (current.visible ? { ...current, visible: false, content: null } : current));
    }, []);

    const tooltip = <GridTooltipPortal tooltipRef={tooltipRef} state={state} />;

    return { tooltipRef, showTooltip, hideTooltip, tooltip };
}

export function GridTooltipProvider({ children, showTooltip, hideTooltip }) {
    const value = useMemo(() => ({ showTooltip, hideTooltip }), [hideTooltip, showTooltip]);
    return <GridTooltipContext.Provider value={value}>{children}</GridTooltipContext.Provider>;
}

export function useGridTooltipActions() {
    return useContext(GridTooltipContext);
}
